import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { produce } from 'immer';
import { toast } from 'material-react-toastify';

import { ROUTES } from '@/lib/routes';
import { ContentStatus, JourneyHub } from '@addmile/backend-client';
import * as Sentry from '@sentry/react';

import { journeyPath } from '../../const';
import { count } from '../../count';
import {
  JourneyDraft,
  countBlocks,
  countSteps,
  newJourneyDraft,
  newMileDraft,
  newStepDraft,
  toJourneyDraft,
  toSaveRequest,
} from '../../draft';
import { describeIssue } from '../../issues';
import {
  DeleteTarget,
  INFO_SELECTION,
  Selection,
  locate,
  reselect,
  selectionAfterDelete,
} from '../../selection';
import {
  journeyErrorMessage,
  useDuplicateJourney,
  useSaveJourney,
} from '../../services/JourneyService';
import CantPublishModal from '../CantPublishModal/CantPublishModal';
import TakeOfflineModal from '../TakeOfflineModal/TakeOfflineModal';
import DeleteNodeModal from './DeleteNodeModal/DeleteNodeModal';
import EditorHeader from './EditorHeader/EditorHeader';
import JourneyTree from './JourneyTree/JourneyTree';
import LockedBanner from './LockedBanner/LockedBanner';
import UnsavedChangesModal from './UnsavedChangesModal/UnsavedChangesModal';
import BlockPanel from './panels/BlockPanel';
import JourneyCompletePanel from './panels/JourneyCompletePanel';
import JourneyInfoPanel from './panels/JourneyInfoPanel';
import MileCompletePanel from './panels/MileCompletePanel';
import MilePanel from './panels/MilePanel';
import StepPanel from './panels/StepPanel';

interface JourneyEditorViewProps {
  // Undefined for a journey that hasn't been saved yet.
  journey?: JourneyHub;
}

const sameContent = (a: JourneyDraft, b: JourneyDraft) =>
  JSON.stringify(toSaveRequest(a)) === JSON.stringify(toSaveRequest(b));

const JourneyEditorView = ({ journey }: JourneyEditorViewProps) => {
  const navigate = useNavigate();
  const saveJourney = useSaveJourney();
  const duplicateJourney = useDuplicateJourney();

  const [saved, setSaved] = useState(journey);
  const [draft, setDraft] = useState(() => (journey ? toJourneyDraft(journey) : newJourneyDraft()));
  const [selection, setSelection] = useState<Selection>(INFO_SELECTION);
  const [isLeaving, setIsLeaving] = useState(false);
  const [notPublished, setNotPublished] = useState<JourneyHub>();
  const [pendingDelete, setPendingDelete] = useState<DeleteTarget>();
  const [pendingOffline, setPendingOffline] = useState<ContentStatus>();

  const update = useCallback(
    (recipe: (draft: JourneyDraft) => void) => setDraft((current) => produce(current, recipe)),
    [],
  );

  const savedDraft = useMemo(() => (saved ? toJourneyDraft(saved) : newJourneyDraft()), [saved]);
  const isDirty = useMemo(() => !sameContent(draft, savedDraft), [draft, savedDraft]);
  // The backend locks the structure by the stored status, so a switch to offline only frees it
  // once saved.
  const locked = saved?.status === ContentStatus.Online;
  const issues = useMemo(() => saved?.issues.map((issue) => describeIssue(issue, saved)), [saved]);

  useEffect(() => {
    if (!isDirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty]);

  const backToList = () => navigate(ROUTES.CONTENT_JOURNEYS);

  const setStatus = (status: ContentStatus) => update((d) => void (d.status = status));

  const handleStatusChange = (status: ContentStatus) => {
    if (saved?.status === ContentStatus.Online && status !== ContentStatus.Online) {
      setPendingOffline(status);
      return;
    }
    setStatus(status);
  };

  const deleteNode = (target: DeleteTarget) => {
    const location = locate(draft, target);
    if (!location) return;

    const { mileIndex, stepIndex, blockIndex } = location;
    update((d) => {
      if (target.kind === 'mile') {
        d.miles.splice(mileIndex, 1);
      } else if (target.kind === 'step') {
        d.miles[mileIndex].steps.splice(stepIndex, 1);
      } else {
        d.miles[mileIndex].steps[stepIndex].blocks.splice(blockIndex, 1);
      }
    });
    setSelection((current) => selectionAfterDelete(current, target));
  };

  const save = async () => {
    try {
      const result = await saveJourney.mutateAsync({
        id: saved?.id,
        journey: toSaveRequest(draft),
        savedStatus: saved?.status,
      });
      const next = toJourneyDraft(result);
      const publishRefused =
        draft.status === ContentStatus.Online && result.status !== ContentStatus.Online;

      setSelection((current) => reselect(current, draft, next));
      setSaved(result);
      setDraft(next);

      if (!saved) {
        // The editor reopens at the new journey's address, so a modal wouldn't survive it.
        if (publishRefused) {
          toast.warning(
            `Saved as a draft: ${count(result.issues.length, 'thing')} to fix before it can go online`,
          );
        } else {
          toast.success('Journey created');
        }
        navigate(journeyPath(result.id), { replace: true });
        return true;
      }

      if (publishRefused) {
        setNotPublished(result);
      } else {
        toast.success('Journey saved');
      }
      return true;
    } catch (error) {
      Sentry.captureException(error);
      toast.error(journeyErrorMessage(error, 'Failed to save the journey'));
      return false;
    }
  };

  const duplicate = async () => {
    if (!saved) return;

    try {
      const copy = await duplicateJourney.mutateAsync(saved.id);
      toast.success(`"${copy.title}" created as a draft`);
      navigate(journeyPath(copy.id));
    } catch (error) {
      Sentry.captureException(error);
      toast.error(journeyErrorMessage(error, 'Failed to duplicate the journey'));
    }
  };

  // An issue can sit far down the page, so bring the panel it opens into view.
  const openIssue = (target: Selection) => {
    setSelection(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addMile = () => {
    const mile = newMileDraft(draft.miles.length);
    update((d) => void d.miles.push(mile));
    setSelection({ kind: 'mile', mileKey: mile.key });
  };

  const addStep = (mileKey: string) => {
    const step = newStepDraft();
    update((d) => void d.miles.find((mile) => mile.key === mileKey)?.steps.push(step));
    setSelection({ kind: 'step', mileKey, stepKey: step.key });
  };

  const renderPanel = () => {
    const location = locate(draft, selection);
    const panelProps = {
      draft,
      update,
      locked,
      onSelect: setSelection,
      onDelete: setPendingDelete,
    };

    if (location && selection.kind === 'journeyComplete') {
      return <JourneyCompletePanel draft={draft} update={update} />;
    }
    if (location && selection.kind === 'mile') {
      return <MilePanel {...panelProps} mileIndex={location.mileIndex} onAddStep={addStep} />;
    }
    if (location && selection.kind === 'mileComplete') {
      return <MileCompletePanel draft={draft} update={update} mileIndex={location.mileIndex} />;
    }
    if (location && selection.kind === 'step') {
      return (
        <StepPanel {...panelProps} mileIndex={location.mileIndex} stepIndex={location.stepIndex} />
      );
    }
    if (location && selection.kind === 'block') {
      return <BlockPanel {...panelProps} {...location} />;
    }

    return (
      <JourneyInfoPanel
        draft={draft}
        update={update}
        issues={issues}
        isDirty={isDirty}
        onSelect={openIssue}
      />
    );
  };

  return (
    <div className="w-full max-w-[1240px] p-6 grid gap-4 content-start">
      <UnsavedChangesModal
        isOpen={isLeaving}
        isSaving={saveJourney.isPending}
        onStay={() => setIsLeaving(false)}
        onDiscard={backToList}
        onSaveAndLeave={async () => {
          if (await save()) backToList();
        }}
      />
      <DeleteNodeModal
        draft={draft}
        target={pendingDelete}
        onConfirm={() => {
          if (pendingDelete) deleteNode(pendingDelete);
          setPendingDelete(undefined);
        }}
        onClose={() => setPendingDelete(undefined)}
      />
      <TakeOfflineModal
        journeyTitle={draft.title}
        status={pendingOffline}
        onConfirm={() => {
          if (pendingOffline) setStatus(pendingOffline);
          setPendingOffline(undefined);
        }}
        onClose={() => setPendingOffline(undefined)}
      />
      <CantPublishModal
        journey={notPublished}
        onClose={() => setNotPublished(undefined)}
        onSelect={openIssue}
      />

      <EditorHeader
        title={draft.title || 'Untitled journey'}
        counts={[
          count(draft.miles.length, 'mile'),
          count(countSteps(draft), 'step'),
          count(countBlocks(draft), 'block'),
        ].join(' · ')}
        status={draft.status}
        canSave={!saved || isDirty}
        isSaving={saveJourney.isPending}
        onBack={() => (isDirty ? setIsLeaving(true) : backToList())}
        onStatusChange={handleStatusChange}
        onSave={save}
      />

      {locked && (
        <LockedBanner
          canDuplicate={!isDirty}
          isDuplicating={duplicateJourney.isPending}
          onDuplicate={duplicate}
        />
      )}

      <div className="grid grid-cols-[300px_1fr] gap-4 items-start">
        <JourneyTree
          draft={draft}
          selection={selection}
          issuesCount={saved?.issues.length ?? 0}
          locked={locked}
          onSelect={setSelection}
          onAddMile={addMile}
          onAddStep={addStep}
        />
        <div className="rounded-2xl bg-white-100 p-5 min-w-0">{renderPanel()}</div>
      </div>
    </div>
  );
};

export default JourneyEditorView;
